import React from 'react';
import styled from 'styled-components';
import {useHtmlDir} from '../../../LegacyWebCoreConfigContext';
import Icon, {IconSize} from '../../Icon/Icon';
import {GenericIconName} from '../../Icon/IconName';
import ClientSideLink from '../../Navigation/ClientSideLink/ClientSideLink';
import {
  bordersColor,
  complementaryColor,
  darkGrayColor,
} from '../../constants/colors';
import {
  mediaMaxTablet,
  mediaMinDesktop,
  mediaMinTablet,
  mobileOnlyCss,
} from '../../constants/customMedia';
import {gridGapPx} from '../../constants/misc';
import {fsM, fwHeavy, fwMediumBold} from '../../constants/typography';
import {useContactFormState} from '../hooks/useContactFormState';

const BreadcrumbLabel = styled(ClientSideLink)`
  font-size: 14px;
  color: ${darkGrayColor};
  margin-bottom: 2px;
  text-decoration: none;
  ${fwMediumBold}
  @media ${mediaMinTablet} {
    margin-bottom: 10px;
  }
  &:last-child {
    margin-bottom: 0;
  }
`;
const BreadcrumbTitle = styled.h1`
  font-size: ${fsM}px;
  ${fwHeavy};

  @media ${mediaMinTablet} {
    margin-top: 10px;
    font-size: 32px;
  }
`;

const MobileOnlyBackButton = styled.button`
  position: absolute;
  left: 8px;
  top: 50%;
  margin: ${-IconSize.M * 0.5}px 0 0;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  ${mobileOnlyCss};
`;

const Breadcrumb = styled.div`
  position: relative;
  padding: 0 0 34px 0;
  box-sizing: border-box;
  width: 100%;

  @media ${mediaMaxTablet} {
    padding: 12px 0 12px 46px;
    background: ${complementaryColor};
    border-bottom: 1px solid ${bordersColor};
    margin-bottom: ${gridGapPx};
  }

  @media ${mediaMinDesktop} {
    padding-bottom: 38px;
  }
`;

const RightArrowIcon = styled(Icon)<{$isRtl?: boolean}>`
  ${({$isRtl}) => $isRtl && 'transform: rotate(180deg);'}
  display: inline;
  margin: 0 6px 0;
  position: relative;
  top: -1px;
`;

const MobileBackButtonIcon = styled(Icon)<{$isRtl?: boolean}>`
  ${({$isRtl}) => $isRtl && 'transform: rotate(180deg);'}
`;
interface BreadcrumbProps {
  text?: string;
  translations: {
    help: string;
    contact_form: string;
    contact_url: string;
  };
}

const handleBackbutton = () => {
  if (history.length > 3) {
    history.go(-1);
  } else {
    history.pushState({}, '', '/h');
  }
};

const HelpdeskSectionBreadcrumb: React.FC<BreadcrumbProps> = props => {
  const {
    text,
    translations: {help, contact_form, contact_url},
  } = props;

  const formState = useContactFormState();
  const isRtlHtmlDir = useHtmlDir() === 'rtl';

  return (
    <Breadcrumb>
      <MobileOnlyBackButton onClick={handleBackbutton}>
        <MobileBackButtonIcon
          data-testid="HelpCenterSectionPageBackButton"
          size={IconSize.M}
          asset={GenericIconName.IcArrowLeft}
          $isRtl={isRtlHtmlDir}
        />
      </MobileOnlyBackButton>
      <BreadcrumbLabel data-testid="HelpCenterSectionPageBreadcrumb" href="/h">
        {help}
      </BreadcrumbLabel>
      {text && formState != null && (
        <>
          <RightArrowIcon
            asset={GenericIconName.IcChevronRight}
            size={IconSize.XXS}
            $isRtl={isRtlHtmlDir}
          />
          <BreadcrumbLabel
            data-testid="HelpCenterSectionPageContactTitle"
            href={contact_url}
          >
            {contact_form}
          </BreadcrumbLabel>
        </>
      )}
      {text && (
        <BreadcrumbTitle data-testid="HelpCenterSectionPageTitle">
          {text}
        </BreadcrumbTitle>
      )}
    </Breadcrumb>
  );
};

export default HelpdeskSectionBreadcrumb;
